import { AddressType } from '@helper/address-type';
import { PreferenceKey } from '@helper/preference-key';
import { SettlementRecord_State } from '@proto/api/customers/resources';

export enum RoutePaths {
  ROOT = '',
  HOME = 'home',
  CART = 'cart',
  SALES_EVENT = 'onsale',
  OUTFIT = 'outfit',
  GALLERY = 'gallery',
  CATEGORY = 'category',
  SPECIAL = 'specialissue',
  STAGGERED = 'staggered',
  SHOP_WINDOW = 'shopwindow',
  PRODUCT = 'product',
  SEARCH = 'search',
  PAGE = 'page',
  PAGE_UPPERCASE = 'Page',
  USER = 'member',
  USER_PPOFILE = 'userprofile',
  LOGIN = 'login',
  FORGOT_ACCOUNT = 'forgotaccount',
  FORGOT_PASSWORD = 'forgotpassword',
  RESET_PASSWORD = 'resetpassword',
  NOTIFICATION = 'notification',
  FAVORITE = 'favorite',
  INVOICE_SETTING = 'invoicesetting',
  REMINDER = 'reminder',
  ORDERS = 'orders',
  PROFILE = 'profile',
  EDIT_PHONE = 'editphone',
  BIND_PHONE = 'bindphone',
  BIND_INVOICE = 'bindinvoice',
  SETTINGS = 'settings',
  PROMOTION = 'promotion',
  LOGISTICS = 'logistics',
  CS_RECORD = 'csrecord',
  SHIPPING = 'shipping',
  PAYMENT = 'payment',
  PAYMENT_RESULT = 'paymentresult',
  ADDRESS = 'address',
  RECIPIENT = 'recipient',
  CHOOSE_RECEIPT = 'choosereceipt',
  SUPPORT_SERVICE = 'supportservice',
  QA_TYPE_LIST = 'qatypelist',
  QA_CONTENT = 'qacontent',
  APP_QA_CONTENT = 'appqacontent',
  ORDER_DETAIL = 'orderdetail',
  REFUND_DETAIL = 'refunddetail',
  CUSTOMER_SERVICE = 'customerservice',
  ONLINE_CS = 'onlinecs',
  RETURN_DETAIL = 'returndetail',
  LOGISTICS_DETAIL = 'logisticsdetail',
  RECEIPT_DETAIL = 'receiptdetail',
  RATE_DETAIL = 'ratedetail',
  COMMENTS = 'comments',
  APPLY = 'apply',
  FINISH = 'finish',
  CREDIT_CARD = 'creditcard',
  PREVIEW = 'preview',
  REFUND_ACCOUNT = 'refundaccount',
  APPLY_SETTLEMENT = 'applysettlement',
  SETTLEMENT_RECORD = 'settlementrecord',
  ACCOUNT_LIST = 'accountlist',
  ACCOUNT_EDIT = 'accountedit',
  APPLY_ACCOUNT = 'applyaccount',
  SELECT_EDIT_ACCOUNT = 'accountselectedit',
  SELECT_ACCOUNT = 'selectaccount',
  UPGRADE = 'upgrade',
  OPEN_APP = 'openApp',
}

// ref app-routing module.
export class RouteHelper {
  static setRedirectUrl(url: string): void {
    try {
      localStorage?.setItem(PreferenceKey.REDIRECT_URL, url);
    } catch {
      // ignore
    }
  }

  static getRedirectUrl(): string | null {
    try {
      const url = localStorage?.getItem(PreferenceKey.REDIRECT_URL);
      if (url === this.refundAccount()) {
        // 從退款帳戶, 嘗試其它帳號登入後, 導回不用再驗證一次簡訊.
        localStorage?.setItem(PreferenceKey.CAN_READ_PROFILE, 't');
      }
      localStorage?.removeItem(PreferenceKey.REDIRECT_URL);
      return url;
    } catch {
      return null;
    }
  }

  static login(): string {
    return RoutePaths.LOGIN;
  }

  static user(): string {
    return RoutePaths.USER;
  }

  static userProfile(): string {
    return RoutePaths.USER_PPOFILE;
  }

  static notification(index?: number): string {
    return index !== undefined
      ? `${RoutePaths.USER}/${RoutePaths.NOTIFICATION}?type=${index}`
      : `${RoutePaths.USER}/${RoutePaths.NOTIFICATION}`;
  }

  static promotion(): string {
    return `${RoutePaths.USER}/${RoutePaths.NOTIFICATION}/${RoutePaths.PROMOTION}`;
  }

  static notificationLogistics(): string {
    return `${RoutePaths.USER}/${RoutePaths.NOTIFICATION}/${RoutePaths.LOGISTICS}`;
  }

  static notificationProduct(): string {
    return `${RoutePaths.USER}/${RoutePaths.NOTIFICATION}/${RoutePaths.PRODUCT}`;
  }

  static csRecord(): string {
    return `${RoutePaths.USER}/${RoutePaths.NOTIFICATION}/${RoutePaths.CS_RECORD}`;
  }

  static favorite(): string {
    return `${RoutePaths.USER}/${RoutePaths.FAVORITE}`;
  }

  static reminder(): string {
    return `${RoutePaths.USER}/${RoutePaths.REMINDER}`;
  }

  static orders(state = 0): string {
    if (state) {
      return `${RoutePaths.USER}/${RoutePaths.ORDERS}/${state}`;
    }
    return `${RoutePaths.USER}/${RoutePaths.ORDERS}`;
  }

  static refundAccount(): string {
    return `${RoutePaths.USER}/${RoutePaths.REFUND_ACCOUNT}`;
  }

  static applySettlement(): string {
    return `${RoutePaths.USER}/${RoutePaths.APPLY_SETTLEMENT}`;
  }

  static editRefundAccount(id: string): string {
    return `${RoutePaths.USER}/${RoutePaths.ACCOUNT_EDIT}/${id}`;
  }

  static editSelectRefundAccount(id: string): string {
    return `${RoutePaths.USER}/${RoutePaths.SELECT_EDIT_ACCOUNT}/${id}`;
  }

  static createRefundAccount(): string {
    return `${RoutePaths.USER}/${RoutePaths.ACCOUNT_EDIT}`;
  }

  static createApplyAccount(): string {
    return `${RoutePaths.USER}/${RoutePaths.APPLY_ACCOUNT}`;
  }

  static settlementRecord(state?: SettlementRecord_State): string {
    if (state) {
      return `${RoutePaths.USER}/${RoutePaths.SETTLEMENT_RECORD}/${state}`;
    }
    return `${RoutePaths.USER}/${RoutePaths.SETTLEMENT_RECORD}`;
  }

  static refundDetail(orderId: string): string {
    return `${RoutePaths.ORDERS}/${RoutePaths.REFUND_DETAIL}/${orderId}`;
  }

  static customerService(id: string): string {
    return `/${RoutePaths.CUSTOMER_SERVICE}/${id}`;
  }

  static returnDetail(orderId: string): string {
    return `${RoutePaths.ORDERS}/${RoutePaths.RETURN_DETAIL}/${orderId}`;
  }

  static logisticDetail(orderId: string): string {
    return `${RoutePaths.ORDERS}/${RoutePaths.LOGISTICS_DETAIL}/${orderId}`;
  }

  static applyReturnFinish(orderId: string): string {
    return `${RoutePaths.ORDERS}/${RoutePaths.FINISH}/${orderId}`;
  }

  static applyReturn(orderId: string, returnId = ''): string {
    return returnId
      ? `${RoutePaths.ORDERS}/${RoutePaths.APPLY}/${orderId}/${returnId}`
      : `${RoutePaths.ORDERS}/${RoutePaths.APPLY}/${orderId}`;
  }

  static receiptDetail(orderId: string): string {
    return `${RoutePaths.ORDERS}/${RoutePaths.RECEIPT_DETAIL}/${orderId}`;
  }

  static rateDetail(orderId: string): string {
    return `${RoutePaths.ORDERS}/${RoutePaths.RATE_DETAIL}/${orderId}`;
  }

  static profile(): string {
    return `${RoutePaths.USER}/${RoutePaths.PROFILE}`;
  }

  static invoiceSetting(): string {
    return `${RoutePaths.USER}/${RoutePaths.INVOICE_SETTING}`;
  }

  static editPhone(): string {
    return `${RoutePaths.USER}/${RoutePaths.EDIT_PHONE}`;
  }

  static bindPhone(): string {
    return `${RoutePaths.USER}/${RoutePaths.BIND_PHONE}`;
  }

  static bindInvoice(): string {
    return `${RoutePaths.USER}/${RoutePaths.BIND_INVOICE}`;
  }

  static settings(): string {
    return RoutePaths.SETTINGS;
  }

  static home(): string {
    return RoutePaths.ROOT;
  }

  static rating(productId: string): string {
    return productId ? `${RoutePaths.COMMENTS}/${productId}` : RoutePaths.COMMENTS;
  }

  static product(productId: string): string {
    return `${RoutePaths.PRODUCT}/${productId}`;
  }

  static skuGallery(productId: string, colorId: string): string {
    return `${RoutePaths.GALLERY}/${productId}/${colorId}`;
  }

  static cart(): string {
    return RoutePaths.CART;
  }

  static shipping(): string {
    return `${RoutePaths.CART}/${RoutePaths.SHIPPING}`;
  }

  static payment(): string {
    return `${RoutePaths.CART}/${RoutePaths.PAYMENT}`;
  }

  static paymentResult(orderId: string): string {
    return `${RoutePaths.CART}/${RoutePaths.PAYMENT}/${orderId}`;
  }

  static thirdPartyPayment(orderId: string): string {
    return `${RoutePaths.PAYMENT}/${orderId}`;
  }

  static creditCardPayment(orderId: string): string {
    return `${RoutePaths.CREDIT_CARD}/${RoutePaths.PAYMENT}/${orderId}`;
  }

  static recipients(): string {
    // mobile only.
    return `${RoutePaths.CART}/${RoutePaths.RECIPIENT}`;
  }

  static chooseReceipt(): string {
    return `${RoutePaths.CART}/${RoutePaths.CHOOSE_RECEIPT}`;
  }

  static orderDetail(orderNo: string): string {
    return `${RoutePaths.ORDERS}/${RoutePaths.ORDER_DETAIL}/${orderNo}`;
  }

  static csOrderDetail(orderNo: string): string {
    return `${RoutePaths.ORDERS}/${RoutePaths.CUSTOMER_SERVICE}/${orderNo}`;
  }

  static miniHome(bigCategoryNameEn: string): string {
    return `${RoutePaths.HOME}/${RouteHelper.fixedEncodeURIComponent(bigCategoryNameEn)}`;
  }

  static category(bigNameEn: string, mediumNameEn?: string, smallNameEn?: string): string {
    if (mediumNameEn && smallNameEn) {
      return `${RoutePaths.CATEGORY}/${RouteHelper.fixedEncodeURIComponent(
        bigNameEn
      )}/${RouteHelper.fixedEncodeURIComponent(mediumNameEn)}/${RouteHelper.fixedEncodeURIComponent(smallNameEn)}`;
    } else if (mediumNameEn) {
      return `${RoutePaths.CATEGORY}/${RouteHelper.fixedEncodeURIComponent(
        bigNameEn
      )}/${RouteHelper.fixedEncodeURIComponent(mediumNameEn)}`;
    }
    return `${RoutePaths.CATEGORY}/${RouteHelper.fixedEncodeURIComponent(bigNameEn)}`;
  }

  static search(keyword: string, bigNameEn?: string): string {
    if (!keyword) {
      return RoutePaths.SEARCH;
    }
    if (bigNameEn) {
      return `${RoutePaths.SEARCH}/${RouteHelper.fixedEncodeURIComponent(
        keyword
      )}/${RouteHelper.fixedEncodeURIComponent(bigNameEn)}`;
    }
    return `${RoutePaths.SEARCH}/${RouteHelper.fixedEncodeURIComponent(keyword)}`;
  }

  static specialIssue(pageNo: string, tabNo?: string): string {
    if (pageNo && tabNo) {
      return `${RoutePaths.SPECIAL}/${RouteHelper.fixedEncodeURIComponent(pageNo)}/${tabNo}`;
    }
    return `${RoutePaths.SPECIAL}/${RouteHelper.fixedEncodeURIComponent(pageNo)}`;
  }

  static shopWindow(keyword: string, bigNameEn?: string): string {
    if (bigNameEn) {
      return `${RoutePaths.SHOP_WINDOW}/${RouteHelper.fixedEncodeURIComponent(
        keyword
      )}/${RouteHelper.fixedEncodeURIComponent(bigNameEn)}`;
    }
    return `${RoutePaths.SHOP_WINDOW}/${RouteHelper.fixedEncodeURIComponent(keyword)}`;
  }

  static salesEvent(eventId: string, bigCategoryNameEn?: string): string {
    if (bigCategoryNameEn) {
      return `${RoutePaths.SALES_EVENT}/${RouteHelper.fixedEncodeURIComponent(
        eventId
      )}/${RouteHelper.fixedEncodeURIComponent(bigCategoryNameEn)}`;
    }
    return `${RoutePaths.SALES_EVENT}/${RouteHelper.fixedEncodeURIComponent(eventId)}`;
  }

  static homeSalesEvent(eventId: string, bigCategoryNameEn?: string): string {
    if (bigCategoryNameEn) {
      return `${RoutePaths.HOME}/${RoutePaths.SALES_EVENT}/${RouteHelper.fixedEncodeURIComponent(
        eventId
      )}/${RouteHelper.fixedEncodeURIComponent(bigCategoryNameEn)}`;
    }
    return `${RoutePaths.HOME}/${RoutePaths.SALES_EVENT}/${RouteHelper.fixedEncodeURIComponent(eventId)}`;
  }

  static page(pageId: string): string {
    return `${RoutePaths.PAGE}/${RouteHelper.fixedEncodeURIComponent(pageId)}`;
  }

  static outfit(id?: string, index?: number): string {
    if (id && index !== undefined) {
      return `${RoutePaths.OUTFIT}/${RouteHelper.fixedEncodeURIComponent(id)}/${RouteHelper.fixedEncodeURIComponent(
        index
      )}`;
    } else if (id) {
      return `${RoutePaths.OUTFIT}/${RouteHelper.fixedEncodeURIComponent(id)}`;
    }
    return RoutePaths.OUTFIT;
  }

  static profileAddress(addressType?: AddressType): string {
    if (addressType) {
      return `${RoutePaths.USER}/${RoutePaths.ADDRESS}/${addressType}`;
    }
    return `${RoutePaths.USER}/${RoutePaths.ADDRESS}`;
  }

  static addressManage(): string {
    return RoutePaths.ADDRESS;
  }

  static createAddress(path: RoutePaths, type: AddressType): string {
    // mobile only.
    return `${RoutePaths.ADDRESS}/${path}/${type}`;
  }

  static editAddress(fromCart: boolean, type: AddressType, id?: string): string {
    // mobile only.
    if (!id) {
      return RoutePaths.ADDRESS;
    }
    const path = fromCart ? RoutePaths.CART : RoutePaths.ADDRESS;
    return `${RoutePaths.ADDRESS}/${path}/${type}/0/${id}`;
  }

  static paymentStoreAddress(storeType = 0): string {
    return `${RoutePaths.ADDRESS}/${RoutePaths.PAYMENT}/${AddressType.STORE_ADDRESS}/${storeType}`;
  }

  static paymentOverseaAddress(countryName: string): string {
    return `${RoutePaths.ADDRESS}/${RoutePaths.PAYMENT}/${
      AddressType.OVERSEA_ADDRESS
    }/${RouteHelper.fixedEncodeURIComponent(countryName)}`;
  }

  static paymentHomeAddress(): string {
    return `${RoutePaths.ADDRESS}/${RoutePaths.PAYMENT}/${AddressType.ADDRESS}`;
  }

  static cartStoreAddress(storeType = 0, id = ''): string {
    return `${RoutePaths.ADDRESS}/${RoutePaths.CART}/${AddressType.STORE_ADDRESS}/${storeType}/${id}`;
  }

  static cartOverseaAddress(countryName: string, id = ''): string {
    return `${RoutePaths.ADDRESS}/${RoutePaths.CART}/${
      AddressType.OVERSEA_ADDRESS
    }/${RouteHelper.fixedEncodeURIComponent(countryName)}/${id}`;
  }

  static cartHomeAddress(id = ''): string {
    return `${RoutePaths.ADDRESS}/${RoutePaths.CART}/${AddressType.ADDRESS}/0/${id}`;
  }

  static supportService(qaType?: string, qaName?: string): string {
    if (qaType) {
      return `${RoutePaths.SUPPORT_SERVICE}/${
        qaType === RoutePaths.QA_TYPE_LIST ? qaType : RouteHelper.fixedEncodeURIComponent(qaType)
      }`;
    }
    if (qaName) {
      return `${RoutePaths.QA_CONTENT}/${RouteHelper.fixedEncodeURIComponent(qaName)}`;
    }
    return RoutePaths.SUPPORT_SERVICE;
  }

  static bankAccountList(): string {
    return `${RoutePaths.USER}/${RoutePaths.ACCOUNT_LIST}`;
  }

  static selectBankAccount(): string {
    return `${RoutePaths.USER}/${RoutePaths.SELECT_ACCOUNT}`;
  }

  static fixedEncodeURIComponent(str: string | number | boolean): string {
    return encodeURIComponent(str).replace(/[!'()*]/g, c => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
  }
}
